import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Video, Stethoscope, FlaskConical, Pill, ScanLine, Ambulance, ArrowRight, FileText, MapPin } from "lucide-react";

export default function PatientDashboard() {
  const { user } = useAuth();
  const [apts, setApts] = useState([]);
  const [labs, setLabs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [emer, setEmer] = useState({ beds: [], ambulances: [] });

  useEffect(() => {
    api.get("/appointments").then((r) => setApts(r.data));
    api.get("/lab-bookings").then((r) => setLabs(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/emergency/my-bookings").then((r) => setEmer(r.data));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Patient dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Welcome, {user?.name?.split(" ")[0]}</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <QuickAction to="/book-opd" icon={Stethoscope} label="Book OPD" color="bg-teal-500" testid="qa-opd" />
        <QuickAction to="/prescription" icon={ScanLine} label="Scan Prescription" color="bg-cyan-500" testid="qa-rx" />
        <QuickAction to="/reports" icon={FileText} label="My Reports" color="bg-slate-700" testid="qa-reports" />
        <QuickAction to="/emergency" icon={Ambulance} label="Emergency Care" color="bg-red-600" testid="qa-emergency" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="My appointments" empty="No appointments yet" items={apts} testid="patient-appointments">
          {apts.map((a) => (
            <div key={a.id} data-testid={`apt-item-${a.id}`} className="flex items-center justify-between py-3 border-b last:border-0">
              <div>
                <div className="font-semibold">{a.doctor_name}</div>
                <div className="text-xs text-slate-500">{a.specialization} · {new Date(a.slot_time).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">{a.mode.replace("_", " ")}</Badge>
                {a.mode === "video" && a.video_room && (
                  <Link to={`/video/${a.video_room}`}>
                    <Button size="sm" variant="outline" data-testid={`join-video-${a.id}`}>
                      <Video className="w-4 h-4 mr-1" />Join
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="Lab bookings" empty="No lab tests booked" items={labs} testid="patient-labs">
          {labs.map((l) => (
            <div key={l.id} className="flex items-center justify-between py-3 border-b last:border-0">
              <div>
                <div className="font-semibold">{l.lab_name}</div>
                <div className="text-xs text-slate-500">{l.tests?.join(", ")} · {l.mode.replace("_", " ")}</div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 border-0">₹{l.amount}</Badge>
            </div>
          ))}
        </Panel>

        <Panel title="Medicine orders" empty="No orders" items={orders} testid="patient-orders">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between py-3 border-b last:border-0">
              <div>
                <div className="font-semibold">{o.pharmacy_name}</div>
                <div className="text-xs text-slate-500">{o.medicines?.length} items · ETA {o.eta_minutes}m</div>
              </div>
              <Badge className="bg-amber-100 text-amber-800 border-0">₹{o.amount}</Badge>
            </div>
          ))}
        </Panel>

        <Panel title="Emergency bookings" empty="No emergency records" items={[...emer.beds, ...emer.ambulances]} testid="patient-emergency">
          {emer.beds.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-3 border-b last:border-0">
              <div>
                <div className="font-semibold">{b.hospital_name}</div>
                <div className="text-xs text-slate-500 capitalize">{b.bed_type} bed reserved</div>
              </div>
              <Badge className="bg-red-100 text-red-800 border-0">{b.status}</Badge>
            </div>
          ))}
          {emer.ambulances.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-3 border-b last:border-0">
              <div>
                <div className="font-semibold">Ambulance {a.vehicle_number}</div>
                <div className="text-xs text-slate-500">{a.ambulance_type.toUpperCase()} · ETA {a.eta_minutes}m</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-800 border-0">{a.status}</Badge>
                <Link to={`/track/${a.id}`}>
                  <Button size="sm" variant="outline" data-testid={`track-${a.id}`}>
                    <MapPin className="w-4 h-4 mr-1" />Track
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, color, testid }) {
  return (
    <Link to={to} data-testid={testid}>
      <Card className="p-5 card-lift rounded-2xl cursor-pointer flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl ${color} text-white flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold">{label}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </Card>
    </Link>
  );
}

function Panel({ title, items, empty, children, testid }) {
  return (
    <Card className="p-5 rounded-2xl" data-testid={testid}>
      <div className="font-display font-bold mb-2">{title}</div>
      {items?.length ? children : <div className="text-sm text-slate-500 py-8 text-center">{empty}</div>}
    </Card>
  );
}
